import React, { useState, useEffect } from 'react';
import { Search, Grid, List, ChevronLeft, ChevronRight } from 'lucide-react';
import { axiosFetch } from 'services/apiService';
import Users from './Users';
import Apps from './Apps';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || "";

interface ApplicationDto {
  id: number;
  name: string;
  hasAccess: boolean;
}

interface User {
  id: number;
  name: string;
  email: string;
  applications: ApplicationDto[];
}

interface PermissionChange {
  userId: number;
  applicationId: number;
  hasAccess: boolean;
}

function AdminPanel(panel: string = 'Users') {
  const [activeTab, setActiveTab] = useState(panel);
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [availableApplications, setAvailableApplications] = useState<ApplicationDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pendingChanges, setPendingChanges] = useState<PermissionChange[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const itemsPerPage = 10;

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosFetch.get(`${API_URL}/Userspermisions`);
      const data = response.data;
      
      setUsers(data.users);
      setAvailableApplications(data.availableApplications);
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Error loading users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const saveAllChanges = async () => {
    setSaving(true);
    setError(null);
    
    try {
      const response = await axiosFetch.put(`${API_URL}/Userspermisions/bulk-update`, {
        changes: pendingChanges
      });
      console.log('Server response:', response.data);
      setPendingChanges([]);
      await loadUsers();
      
    } catch (error) {
      console.error('Error saving changes:', error);
      setError('Error saving changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const discardChanges = () => {
    setPendingChanges([]);
    setError(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading data...</p>
        </div>
      </div>
    );
  }

  if (error && users.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-4">⚠️</div>
          <p className="text-gray-500 mb-4">{error}</p>
          <button 
            onClick={loadUsers}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Error message */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
            <span className="block sm:inline">{error}</span>
            <button
              className="absolute top-0 bottom-0 right-0 px-4 py-3"
              onClick={() => setError(null)}
            >
              <span className="sr-only">Dismiss</span>
              ×
            </button>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex space-x-8">
              <Link
                to={'/admin'}
                className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'Users'
                    ? 'text-blue-600 border-blue-600'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
              >
                Users
              </Link>
              <Link
                to={'/admin/apps'}
                className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'Apps'
                    ? 'text-blue-600 border-blue-600'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}>
                Apps
              </Link>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              
              <div className="flex bg-blue-600 rounded">
                <button className="p-2 text-white">
                  <Grid className="w-4 h-4" />
                </button>
                <button className="p-2 text-blue-200 hover:text-white">
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
          {activeTab === 'Users' &&
            <Users userList={users} availableApplications={availableApplications} pendingChanges={pendingChanges} setPendingChanges={setPendingChanges} />
          }
          {activeTab === 'Apps' &&
            <Apps />
          }
        </div>
      </div>

      {/* Floating Save Button */}
      {pendingChanges.length > 0 && (
        <div className="fixed bottom-6 right-6 z-50">
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-w-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
                <span className="text-sm font-medium text-gray-900">
                  {pendingChanges.length} unsaved change{pendingChanges.length !== 1 ? 's' : ''}
                </span>
              </div>
              <button
                onClick={discardChanges}
                disabled={saving}
                className="text-gray-400 hover:text-gray-600 text-sm underline disabled:opacity-50"
              >
                Discard
              </button>
            </div>
            <button
              onClick={saveAllChanges}
              disabled={saving}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
            >
              {saving && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              )}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      <button
        onClick={loadUsers}
        disabled={loading}
        className="fixed bottom-6 left-6 p-3 bg-gray-600 text-white rounded-full shadow-lg hover:bg-gray-700 disabled:opacity-50"
        title="Refresh data"
      >
        <div className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`}>
          ↻
        </div>
      </button>
    </div>
  );
}

export default AdminPanel;